import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Collapse } from '@material-ui/core'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import RightPanel from '../../../RightPanel'
import Icon from '../../../../atoms/Icon'
import Markup from '../../../../atoms/Markup'
import FlexRow from '../../../../molecules/FlexRow'
import { labelStyle as detailLabelStyle, valueStyle as detailValueStyle, getTaskStatus } from '../shared/redtailUIHelpers'
import { LinkedContactRow, LinkedContactDetail } from '../shared/LinkedContactDisplay'
import CompleteActivityButton from '../shared/CompleteActivityButton'

function TaskDetail ({ activity }) {
  const startFormatted = activity.start_date
    ? dayjs(activity.start_date).format('MMM D, YYYY')
    : null
  const endFormatted = activity.end_date
    ? dayjs(activity.end_date).format('MMM D, YYYY')
    : null

  return (
    <div style={{ padding: '8px 1.5rem 4px 28px' }}>
      <LinkedContactDetail
        contacts={activity.linked_contacts}
        labelStyle={detailLabelStyle}
        valueStyle={detailValueStyle}
      />

      <div style={detailLabelStyle}>Task Name</div>
      <div style={detailValueStyle}>{activity.subject || 'Untitled Task'}</div>

      {activity.description && (
        <>
          <div style={detailLabelStyle}>Description</div>
          <div style={detailValueStyle}>
            <Markup value={activity.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: '24px' }}>
        {startFormatted && (
          <div>
            <div style={detailLabelStyle}>Start Date</div>
            <div style={detailValueStyle}>{startFormatted}</div>
          </div>
        )}
        {endFormatted && (
          <div>
            <div style={detailLabelStyle}>Due Date</div>
            <div style={detailValueStyle}>{endFormatted}</div>
          </div>
        )}
      </div>

      {activity.priority && (
        <>
          <div style={detailLabelStyle}>Priority</div>
          <div style={detailValueStyle}>{activity.priority}</div>
        </>
      )}

      {activity.location && (
        <>
          <div style={detailLabelStyle}>Location</div>
          <div style={detailValueStyle}>{activity.location}</div>
        </>
      )}
    </div>
  )
}

TaskDetail.propTypes = {
  activity: PropTypes.object.isRequired
}

const METRIC_INVALIDATE_KEYS = [[QUERY_KEYS.redtailDomainQuery, 'metric-preset']]

function TaskRow ({ activity, expanded, onToggle }) {
  const status = getTaskStatus(activity)
  const dueFormatted = activity.end_date
    ? dayjs(activity.end_date).format('MMM D, YYYY')
    : null

  return (
    <div style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
      <div
        role='button'
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 1.5rem',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)'
        }}
        >
          <Icon
            name={status === 'overdue' ? 'clock' : 'radioUnchecked'}
            customSize='18px'
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            <LinkedContactRow contacts={activity.linked_contacts} />
          </div>
          <div style={{
            fontSize: 'var(--font-size-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {activity.subject || 'Untitled Task'}
          </div>
          {dueFormatted && (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
              marginTop: '2px'
            }}
            >
              Due {dueFormatted}
            </div>
          )}
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: 'var(--text-color-subtle)',
          transition: 'transform 150ms',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }}
        >
          <Icon name='chevronDown' customSize='16px' />
        </div>
      </div>
      <Collapse in={expanded} timeout={200}>
        <div style={{
          background: 'linear-gradient(0deg, var(--color-surface-200) 90%, var(--color-surface-300))',
          borderRadius: 'var(--border-radius-sm)',
          marginBottom: '4px'
        }}
        >
          <TaskDetail activity={activity} />
          <div style={{ padding: '0 0 12px 28px' }}>
            <CompleteActivityButton
              activityId={activity.id}
              invalidateQueryKeys={METRIC_INVALIDATE_KEYS}
              size='sm'
              variant='outline'
            >
              Complete
            </CompleteActivityButton>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

TaskRow.propTypes = {
  activity: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired
}

function RedtailTaskPanel ({ open, onClose, items, title }) {
  const [expandedId, setExpandedId] = useState(null)

  const sorted = useMemo(() => {
    if (!items?.length) return []
    return [...items].sort((a, b) => {
      const da = a.end_date ? dayjs(a.end_date).valueOf() : Infinity
      const db = b.end_date ? dayjs(b.end_date).valueOf() : Infinity
      return da - db
    })
  }, [items])

  const handleToggle = useCallback((id) => {
    setExpandedId(prev => prev === id ? null : id)
  }, [])

  return (
    <RightPanel
      open={open}
      toggleOpen={onClose}
      title={title || 'Tasks'}
      showCloseButton
    >
      <div style={{ paddingBottom: '1.5rem' }}>
        {!sorted.length
          ? (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '120px',
                padding: '0 1.5rem',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              No current tasks
            </FlexRow>
          )
          : sorted.map(activity => (
            <TaskRow
              key={activity.id}
              activity={activity}
              expanded={expandedId === activity.id}
              onToggle={() => handleToggle(activity.id)}
            />
          ))}
      </div>
    </RightPanel>
  )
}

RedtailTaskPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  items: PropTypes.array,
  title: PropTypes.string
}

export default RedtailTaskPanel
